import { useCallback, useEffect, useRef } from 'react';
import { Flag, ShieldCheck, Sparkles } from 'lucide-react';
import ChatMascot from './ChatMascot';

/**
 * First-visit welcome modal.
 *
 * Shown once per browser session (sessionStorage), so a user who reloads the tab
 * is not interrupted again, but a fresh session gets the reminder. Purely
 * informational - dismissing it gates nothing.
 *
 * Reuses the empty state's mascot so the visual language stays consistent - and
 * freezes the copy behind it (`motionPaused` in App), so the only thing moving
 * while this is on screen is the mascot inside it.
 */
const SEEN_KEY = 'z2m-chat-welcome-seen';

export function shouldShowWelcome() {
  try {
    return sessionStorage.getItem(SEEN_KEY) !== '1';
  } catch {
    // Storage blocked (private mode): show it rather than silently skipping.
    return true;
  }
}

export function markWelcomeSeen() {
  try {
    sessionStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* ignore */
  }
}

const POINTS = [
  {
    Icon: ShieldCheck,
    title: 'Keep every deal on Z2Market',
    body: 'Payments and messages stay inside the platform. The moment a deal moves off-site, your Z2Shield protection ends.',
  },
  {
    Icon: Sparkles,
    title: 'Watched over, 24/7',
    body: 'Our AI reviews chats around the clock for scam patterns and off-platform attempts - so honest buyers and sellers can trade with confidence.',
  },
  {
    Icon: Flag,
    title: 'Report it, get rewarded',
    body: 'If someone asks for your contact details or pushes an outside deal, report them. You help keep the community safe - and you get rewarded for it.',
  },
];

export default function WelcomeModal({ open, onClose }) {
  const dialogRef = useRef(null);
  const buttonRef = useRef(null);
  const previouslyFocused = useRef(null);

  // Focus the dismiss button on open and restore focus on close.
  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;
    buttonRef.current?.focus();

    return () => {
      const previous = previouslyFocused.current;
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }, [open]);

  /**
   * Escape closes it; Tab is trapped inside the dialog. There is only one
   * control today, but the trap walks the real focusable list so adding another
   * button later cannot leak focus behind the modal.
   */
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!open) return null;

  return (
    <div
      className="animate-z2-fade-in fixed inset-0 z-[70] flex items-center justify-center bg-overlay-strong p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="z2m-welcome-title"
        aria-describedby="z2m-welcome-sub"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={handleKeyDown}
        className="animate-z2-modal-in max-h-[92vh] w-full max-w-[420px] overflow-y-auto rounded-2xl border border-line bg-surface shadow-lift"
      >
        <div className="flex flex-col items-center px-6 pt-6 text-center">
          {/* 132px tall on purpose: the artwork used to be pinned at 186px
              inside this 132px box, so it bled 27px each way and the shield
              body overlapped the heading. The mascot itself now fills the box. */}
          <ChatMascot className="h-[132px] w-[176px]" />

          <h2
            id="z2m-welcome-title"
            className="mt-3 text-[18px] leading-tight font-semibold tracking-tight text-ink"
          >
            Welcome to Z2Market Chat
          </h2>
          <p id="z2m-welcome-sub" className="mt-1.5 text-[12.5px] leading-relaxed text-ink-2">
            Every trade here is backed by <span className="font-semibold text-accent">Z2Shield</span>{' '}
            - our escrow and buyer protection.
          </p>
        </div>

        <ul className="space-y-3 px-6 py-5">
          {POINTS.map(({ Icon, title, body }) => (
            <li key={title} className="flex items-start gap-3">
              <span className="mt-px grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-brand-soft text-accent">
                <Icon size={15} />
              </span>
              <span className="min-w-0">
                <span className="block text-[12.5px] font-semibold text-ink">{title}</span>
                <span className="mt-0.5 block text-[12px] leading-relaxed text-ink-2">{body}</span>
              </span>
            </li>
          ))}
        </ul>

        <div className="px-6 pb-6">
          <button
            ref={buttonRef}
            type="button"
            onClick={onClose}
            className="z2-glow-hover h-11 w-full rounded-xl bg-brand-600 text-[13.5px] font-semibold text-white transition hover:bg-brand-700"
          >
            Got it
          </button>
          <p className="mt-2.5 text-center text-[11px] text-ink-3">
            You can revisit these rules any time in our safety guidelines.
          </p>
        </div>
      </div>
    </div>
  );
}
